/**
 * Helpers for GET /costs/untagged rows — Cost Explorer `resourceId` is often a full ARN;
 * operators need a short label and region to match resources in the AWS console for tagging.
 */

export type ParsedUntaggedResourceId = {
  isArn: boolean;
  /** AWS partition segment (e.g. aws, aws-cn). */
  partition: string | null;
  /** Service code from ARN when parseable (e.g. ec2, s3, lambda). */
  arnService: string | null;
  region: string | null;
  accountId: string | null;
  /** Relative resource id portion after account (instance/i-…, function:name, bucket, etc.). */
  resourcePath: string | null;
  /** Short label suitable for a table column (instance id, bucket name, function name, …). */
  friendlyLabel: string;
  /** Deep link when we can derive one safely from ARN shape; otherwise null. */
  consoleUrl: string | null;
};

function truncateMiddle(value: string, maxLen: number): string {
  if (value.length <= maxLen) {
    return value;
  }
  const head = Math.floor(maxLen / 2) - 1;
  const tail = maxLen - head - 1;
  return `${value.slice(0, head)}…${value.slice(value.length - tail)}`;
}

/**
 * Split a standard AWS ARN into partition, service, region, account, resource.
 * Supports `arn:aws:s3:::bucket` (empty region/account) and `arn:aws:iam::acct:…`.
 */
export function parseUntaggedResourceId(resourceId: string): ParsedUntaggedResourceId {
  const parts = resourceId.split(':');
  const isArnShape =
    parts.length >= 6 && parts[0] === 'arn' && typeof parts[1] === 'string' && parts[1].length > 0;

  if (!isArnShape) {
    return {
      isArn: false,
      partition: null,
      arnService: null,
      region: null,
      accountId: null,
      resourcePath: null,
      friendlyLabel: truncateMiddle(resourceId.trim(), 56),
      consoleUrl: null,
    };
  }

  const partition = parts[1] ?? null;
  const arnService = (parts[2] ?? '').toLowerCase() || null;
  const region = parts[3] !== undefined && parts[3] !== '' ? parts[3] : null;
  const accountId = parts[4] !== undefined && parts[4] !== '' ? parts[4] : null;
  const resourcePath = parts.slice(5).join(':');

  const friendlyLabel = deriveFriendlyLabel(arnService, resourcePath);
  const consoleUrl = deriveConsoleUrl({
    partition: partition ?? 'aws',
    arnService,
    region,
    accountId,
    resourcePath,
    friendlyLabel,
  });

  return {
    isArn: true,
    partition,
    arnService,
    region,
    accountId,
    resourcePath: resourcePath !== '' ? resourcePath : null,
    friendlyLabel,
    consoleUrl,
  };
}

function deriveFriendlyLabel(arnService: string | null, resourcePath: string): string {
  const path = resourcePath.trim();
  if (path === '') {
    return '—';
  }

  if (arnService === 's3') {
    const bucketOnly = path.includes('/') ? path.split('/')[0] : path;
    if (bucketOnly !== undefined && bucketOnly !== '') {
      return bucketOnly.length > 96 ? truncateMiddle(bucketOnly, 96) : bucketOnly;
    }
  }

  if (path.includes('/')) {
    const segments = path.split('/').filter((s) => s !== '');
    const last = segments[segments.length - 1];
    if (last !== undefined && last !== '') {
      return last.length > 96 ? truncateMiddle(last, 96) : last;
    }
  }

  if (arnService === 'lambda' && path.toLowerCase().startsWith('function:')) {
    const rest = path.slice('function:'.length);
    const name = rest.split(':')[0];
    if (name !== undefined && name !== '') {
      return name;
    }
  }

  if (arnService === 'rds' && path.toLowerCase().startsWith('db:')) {
    const name = path.slice(3);
    if (name !== '') {
      return name.split(':')[0] ?? name;
    }
  }

  if (arnService === 'rds' && path.toLowerCase().startsWith('cluster:')) {
    const name = path.slice('cluster:'.length);
    if (name !== '') {
      return name.split(':')[0] ?? name;
    }
  }

  if (path.includes(':')) {
    const tail = path.split(':').pop();
    if (tail !== undefined && tail !== '') {
      return tail.length > 96 ? truncateMiddle(tail, 96) : tail;
    }
  }

  return path.length > 96 ? truncateMiddle(path, 96) : path;
}

type ConsoleParts = {
  partition: string;
  arnService: string | null;
  region: string | null;
  accountId: string | null;
  resourcePath: string;
  friendlyLabel: string;
};

function deriveConsoleUrl(parts: ConsoleParts): string | null {
  const { arnService, region, resourcePath } = parts;

  if (arnService === 's3') {
    const bucket = resourcePath.split('/')[0]?.trim();
    if (bucket !== undefined && bucket !== '') {
      return `https://s3.console.aws.amazon.com/s3/buckets/${encodeURIComponent(bucket)}`;
    }
    return null;
  }

  if (arnService === null || region === null || region === '') {
    return null;
  }

  const encRegion = encodeURIComponent(region);

  if (arnService === 'ec2') {
    const m = /^instance\/(i-[a-f0-9]+)$/i.exec(resourcePath.trim());
    if (m?.[1]) {
      const id = encodeURIComponent(m[1]);
      return `https://${region}.console.aws.amazon.com/ec2/home?region=${encRegion}#InstanceDetails:instanceId=${id}`;
    }
    return null;
  }

  if (arnService === 'rds') {
    const id = encodeURIComponent(parts.friendlyLabel);
    return `https://${region}.console.aws.amazon.com/rds/home?region=${encRegion}#database:id=${id}`;
  }

  if (arnService === 'lambda') {
    const fn = encodeURIComponent(parts.friendlyLabel);
    return `https://${region}.console.aws.amazon.com/lambda/home?region=${encRegion}#/functions/${fn}`;
  }

  if (arnService === 'elasticloadbalancing') {
    const m = /loadbalancer\/(app|net)\/([^/]+)\/([a-f0-9]+)/i.exec(resourcePath);
    if (m?.[2]) {
      const name = encodeURIComponent(m[2]);
      return `https://${region}.console.aws.amazon.com/ec2/home?region=${encRegion}#LoadBalancers:search=${name}`;
    }
  }

  return null;
}

export function formatUntaggedScope(parsed: ParsedUntaggedResourceId): string {
  const bits: string[] = [];
  if (parsed.region !== null && parsed.region !== '') {
    bits.push(parsed.region);
  }
  if (parsed.accountId !== null && parsed.accountId !== '') {
    bits.push(parsed.accountId);
  }
  if (bits.length === 0) {
    return '—';
  }
  return bits.join(' · ');
}

/**
 * Cost Explorer often prefixes usage rows with a short location token (e.g. USW1-…).
 * Map known tokens to an AWS region id for console orientation — not guaranteed for all rows.
 */
const CE_LOCATION_TO_REGION: Record<string, string> = {
  APE1: 'ap-east-1',
  APN1: 'ap-northeast-1',
  APN2: 'ap-northeast-2',
  APN3: 'ap-northeast-3',
  APS1: 'ap-south-1',
  APS2: 'ap-southeast-1',
  APS3: 'ap-southeast-2',
  APS4: 'ap-southeast-3',
  CAN1: 'ca-central-1',
  EUC1: 'eu-central-1',
  EUW1: 'eu-west-1',
  EUW2: 'eu-west-2',
  EUW3: 'eu-west-3',
  EUN1: 'eu-north-1',
  SAE1: 'sa-east-1',
  USE1: 'us-east-1',
  USE2: 'us-east-2',
  USW1: 'us-west-1',
  USW2: 'us-west-2',
  UGE1: 'us-gov-east-1',
  UGW1: 'us-gov-west-1',
};

export function guessAwsRegionFromCeUsageStyleId(resourceId: string): string | null {
  const m = /^([A-Za-z]{3,5}\d*)-/.exec(resourceId.trim());
  if (m?.[1] === undefined) {
    return null;
  }
  const code = m[1].toUpperCase();
  return CE_LOCATION_TO_REGION[code] ?? null;
}

/** Region · account from ARN, or inferred region for common CE usage-style ids, else em dash. */
export function formatUntaggedRegionCell(resourceId: string, parsed: ParsedUntaggedResourceId): string {
  const arnScope = formatUntaggedScope(parsed);
  if (arnScope !== '—') {
    return arnScope;
  }
  const inferred = guessAwsRegionFromCeUsageStyleId(resourceId);
  if (inferred !== null) {
    return `${inferred} · inferred from id prefix`;
  }
  return '—';
}

export type UntaggedNameDisplay = {
  /** Short label for the Name / match column (never an empty em dash for non-duplicate rows). */
  primary: string;
  /** Optional second line — keep short; use `hintDetail` for full guidance (tooltip). */
  hint: string | null;
  /** Longer operator text; shown as `title` on the hint line (and primary when there is no hint). */
  hintDetail: string | null;
};

/**
 * Primary label + optional short hint for the untagged table Name / match column.
 * Avoids showing only "—" above a long paragraph: CE usage rows get a compact title
 * (e.g. "Fargate vCPU usage") and a one-line hint; full text lives in `hintDetail` for tooltips.
 */
export function getUntaggedNameDisplay(
  resourceId: string,
  parsed: ParsedUntaggedResourceId,
  displayName?: string,
): UntaggedNameDisplay {
  const idTrim = resourceId.trim();
  const api = displayName?.trim();
  if (api !== undefined && api !== '' && api !== idTrim) {
    return { primary: api, hint: null, hintDetail: null };
  }

  if (parsed.isArn) {
    if (parsed.friendlyLabel !== '—' && parsed.friendlyLabel !== idTrim) {
      return { primary: parsed.friendlyLabel, hint: null, hintDetail: null };
    }
    const short = truncateMiddle(idTrim, 44);
    return {
      primary: short,
      hint: null,
      hintDetail: idTrim,
    };
  }

  const low = idTrim.toLowerCase();

  if (low.includes('fargate') && (low.includes('vcpu') || low.includes('percpu') || low.includes('hours'))) {
    return {
      primary: 'Fargate vCPU usage',
      hint: 'Not a taggable id — set blooph-cost-app on ECS (cluster / service / task definition).',
      hintDetail:
        'Cost Explorer groups Fargate vCPU-hour spend under this dimension string. You cannot tag this string in AWS. Apply blooph-cost-app on the ECS cluster, service, or task definition (or the capacity that backs this workload).',
    };
  }

  if (low.includes('lambda') && (low.includes('gb-second') || low.includes('gb second') || low.includes('request'))) {
    return {
      primary: 'Lambda usage',
      hint: 'Not a taggable id — tag Lambda functions or related allocation tags.',
      hintDetail:
        'This is a Cost Explorer usage line for Lambda GB-seconds or requests. Tag the Lambda functions or resources that own this spend.',
    };
  }

  if (low.includes('natgateway') || low.includes('nat-gateway') || low.includes('data transfer')) {
    return {
      primary: 'Network / data transfer',
      hint: 'Map to VPC, NAT, or gateways in Cost Explorer or your CMDB, then tag those resources.',
      hintDetail:
        'Cost Explorer surfaces NAT gateway or data-transfer usage under synthetic dimension lines. Tag the VPC, NAT gateway, or related network resources that own this spend.',
    };
  }

  // e.g. USW1-CW:GMD-Metrics — CloudWatch billing dimensions, not a taggable resource id
  if (low.includes(':cw:') || low.includes('-cw:')) {
    const isGmd = low.includes('gmd') || low.includes('getmetricdata');
    return {
      primary: isGmd ? 'CloudWatch GetMetricData' : 'CloudWatch usage',
      hint: 'Region-wide CloudWatch charge — not one ARN; tag workloads indirectly or allocate this pool.',
      hintDetail:
        'Cost Explorer often records CloudWatch spend under usage-type strings (…-CW:…), not per-dashboard or per-metric ARNs. Tagging CloudWatch dashboards or alarms does not split GetMetricData/API cost by app in this view. Reduce high-cardinality metrics and dashboard query frequency, use CUR for attribution, or allocate this shared cost across blooph-cost-app values (for example by fixed percentage).',
    };
  }

  // e.g. USW1-Requests-Tier1 — very often Amazon S3 request pricing in that region
  if (low.includes('requests-tier')) {
    return {
      primary: 'Request tier (often S3)',
      hint: 'Usually S3 LIST/PUT/GET-style request charges — tag buckets; this line is still account/region aggregated.',
      hintDetail:
        'Usage types like Requests-Tier1/Tier2 commonly map to Amazon S3 request pricing for the region prefix (e.g. USW1). Cost Explorer may not list individual bucket ARNs for this row. Tag S3 buckets with blooph-cost-app, then use CE or a CUR grouped by resource (where available) or S3 Storage Lens / access logs to see which buckets drive the spend.',
    };
  }

  return {
    primary: 'Cost Explorer line',
    hint: 'Not an AWS resource ARN — map this line to real resources in Cost Explorer or CUR, then tag.',
    hintDetail:
      'This id is usually a grouped usage, location, or dimension from Cost Explorer. Use Cost Explorer drill-down, cost allocation tags, a CUR export, or resource-level reports to find taggable AWS resources.',
  };
}

export function sortedUntaggedTagEntries(tags: Record<string, string> | undefined): [string, string][] {
  if (tags === undefined) {
    return [];
  }
  return Object.entries(tags).sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}
